import { HStack, type ButtonProps } from '@chakra-ui/react';
import OutlinedButton from '@/components/common/buttons/OutlinedButton';
import { KeyCap } from '@/components/ui/ShortcutHint';
import { useFeatureFlags } from '@/context/FeatureFlagsContext';

interface ReasonChipGroupProps<Reason extends string> {
	/** Most useful first, which is also the one under `1`. */
	order: readonly Reason[];
	labels: Record<Reason, string>;
	chosen: Reason | null;
	onChoose: (reason: Reason) => void;
	/** The group's accessible name; the visible heading belongs to the form. */
	label: string;
}

const CHIP_PROPS = {
	variant: 'outline',
	size: 'sm',
	h: 8,
	px: 3,
	minW: 0,
	gap: 1.5,
	elevated: true,
	fontSize: 'sm',
	color: 'brand.fg',
	_hover: { bg: 'surface.subtle', borderColor: 'border.strong' },
	_focusVisible: {
		outline: '2px solid',
		outlineColor: 'brand.focusRing',
		outlineOffset: '-2px',
	},
} satisfies ButtonProps;

/**
 * The reasons a review form offers, as one row of chips that behaves as a radiogroup.
 *
 * Shared by the discard and keep forms so the two read as the same kind of question. The chips
 * only choose; the form around them decides what a choice leads to. Their keyboard rule lives in
 * `chooseReasonByKey`, which the form calls from its own handler, because the number keys have to
 * work from anywhere in the form and not only while a chip has focus. The number caps are drawn
 * only under `reviewShortcutHints`, like the legend; the keys work either way.
 */
export function ReasonChipGroup<Reason extends string>(props: ReasonChipGroupProps<Reason>) {
	const { order, labels, chosen, onChoose, label } = props;
	const { reviewShortcutHints } = useFeatureFlags();
	return (
		<HStack align="center" gap={2} flexWrap="wrap" role="radiogroup" aria-label={label}>
			{order.map((reason, index) => {
				const isSelected = chosen === reason;
				return (
					<OutlinedButton
						key={reason}
						type="button"
						role="radio"
						aria-checked={isSelected}
						aria-keyshortcuts={String(index + 1)}
						// One tab stop for the group, not one per reason: Tab moves past the reasons, the
						// arrows move between them. Falls to the first while nothing is chosen, so the
						// group is always reachable.
						tabIndex={(chosen ?? order[0]) === reason ? 0 : -1}
						autoFocus={index === 0}
						{...CHIP_PROPS}
						fontWeight={isSelected ? 'semibold' : 'medium'}
						bg={isSelected ? 'surface.subtle' : 'surface.default'}
						borderColor={isSelected ? 'border.strong' : 'border.default'}
						onClick={() => onChoose(reason)}
					>
						{reviewShortcutHints ? <KeyCap decorative>{index + 1}</KeyCap> : null}
						{labels[reason]}
					</OutlinedButton>
				);
			})}
		</HStack>
	);
}
